/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import fetch from 'isomorphic-fetch';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import lodashSize from 'lodash/size';

import axios from 'axios';

import { LoggingHelper, SecurityHelper } from 'sarsaparilla';
import { getAuthHeader, isRegisteredUserLoggedIn } from 'sarsaparilla/auth';

import {
    getCachedLocation,
    setCachedLocation,
} from 'shared-ui/geolocation/location-cache';
import { getLocationWithFallback } from 'shared-ui/geolocation/location-utils';

import * as types from '../constants';
import {
    ACCESS_TYPE,
    AMENITIES_TYPE,
    CAMPING_TYPES,
    CAMPSITE_CAMPING_TYPE,
    CAMPSITE_DAYUSE_TYPE,
    ELECTRIC_TYPE,
    FCFS_TYPE,
    KEY_CHARACTERISTICS_TYPE,
    OCCUPANTS_TYPE,
    SEARCH_FILTER_CAMPSITE_ELECTRICAL,
    SPECIAL_SITE_TYPE,
    VEHICLE_LENGTH_TYPE,
} from '../constants/filters/camping';
import {
    INVENTORY_TYPES_CAMPING,
    INVENTORY_TYPES_DAY_USE,
    INVENTORY_TYPES_FILTERS,
} from '../constants/filters/inventoryTypes';
import { toActivityTagSelection } from '../constants/activityTags';
import { findActivityOption, toActivitySelection } from '../utils/activities';
import { searchDate } from '../utils/dateUtils';
import {
    parseCampsiteFilters,
    parseFgParams,
    removeCampingFromUiFilters,
} from '../utils/filters';
import { cloneState } from '../utils/stateUtils';
import * as utils from '../utils/utils';

const nearByDictionary = ['nearme', 'nearby', 'near me', 'near by'];

export const MAX_SEARCH_RESULTS = 200;
export const DEFAULT_RADIUS = 165; // about 100 miles

const inventoryTypeFilters = [
    {
        key: 'camping',
        value: 'Camping/Lodging',
        filters: [
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'Overnight,na',
            },
            {
                attribute: 'entity_type',
                filterValue: 'campground',
            },
            {
                attribute: '-entity_id',
                filterValue:
                    '(10238170 OR 10238165 OR 10249189 OR 10241987 OR 10241936 OR 10241919 OR 10241911 OR 10241557 OR 10241626 OR 10241695 OR 10241754 OR 10241801 OR 10241839 OR 10241885 OR 10241903)',
            },
        ],
    },
    {
        key: 'dayuse',
        value: 'Day Use',
        filters: [
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'Day',
            },
            {
                attribute: 'entity_type',
                filterValue: 'campground',
            },
        ],
    },
    {
        key: 'tours',
        value: 'Tours',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'tour,timedentry_tour',
            },
        ],
    },
    {
        key: 'tickettours',
        value: 'Tickets',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'ticketfacility,timedentry',
            },
        ],
    },
    {
        key: 'permits',
        value: 'Permits',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'permit,vehiclepermit',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'pass',
        value: 'Pass',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'pass,activitypass',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'activitypass',
        value: 'Activity Pass',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'activitypass',
            },
        ],
    },
    {
        key: 'treepermit',
        value: 'Tree Permits',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'treepermit',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'venuereservations',
        value: 'Venues',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'venuereservations',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'program',
        value: 'Programs',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'program,program_session',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'giftcard',
        value: 'Gift Cards',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'giftcard',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'poi',
        value: 'Points of Interest',
        filters: [
            {
                attribute: 'reservable',
                filterValue: '0',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'recarea',
        value: 'Rec Area',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'recarea',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
];

function isBlank(val) {
    return !val || val === '';
}

// The search services saves dates in the redux store as strings in this format 'MM/DD/YYYY'
// This does literally no one any good, so we have to have this complicated this to unwind it
function getUTCStringFromWeirdSearchFormat(weirdString) {
    const weirdStringPattern = /^(\d{1,2})\/\d{1,2}\/\d\d\d\d/;

    if (typeof weirdString === 'string' && weirdStringPattern.test(weirdString)) {
        const [month, day, year] = weirdString.split('/');

        const utcDate = new Date(Date.UTC(Number(year), Number(month), Number(day)));
        return utcDate.toISOString();
    }

    return undefined;
}

// fetch doesn't handle errors well
function status(response) {
    if (response.status >= 200 && response.status < 300) {
        return Promise.resolve(response);
    }
    LoggingHelper.logError(response.statusText);
    return Promise.reject(new Error(response.statusText));
}

// parse fetch response to json
function parseJson(response) {
    return response.json();
}

// helper method to dispatch UI errors
function searchFailed(message) {
    return {
        type: types.GET_SEARCH_ERROR,
        payload: message,
        error: true,
    };
}

// update checkin/checkout dates
export function updateSelectedDate(time, isCheckin) {
    return (dispatch) => {
        if (isCheckin) {
            return dispatch({
                type: types.UPDATE_CHECKIN_DATE,
                time,
            });
        }

        return dispatch({
            type: types.UPDATE_CHECKOUT_DATE,
            time,
        });
    };
}

// update search criteria state with a new value for a name parameter
export function updateSearchCriteria(name, value) {
    return (dispatch) => {
        return dispatch({
            type: types.UPDATE_SEARCH_CRITERIA,
            name,
            value,
        });
    };
}

// update search criteria state with a new set of values
export function updateSearchCriterias(params) {
    return (dispatch) => {
        const st = { ...params };
        st.type = types.UPDATE_SEARCH_CRITERIAS;
        return dispatch(st);
    };
}

// update search criteria by adding a filter
export function addSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.ADD_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by setting a filter value
export function setSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.SET_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by removing a filter
export function removeSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.REMOVE_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// reset search filters (other than what/where/lat/lng)
export function resetSearchFilters() {
    return {
        type: types.RESET_SEARCH_FILTERS,
    };
}

export function removeTourTimesFilter(options) {
    return (dispatch) => {
        for (const key in options) {
            if (Object.hasOwn(options, key)) {
                dispatch(removeSearchFilter('tourTimes', options[key]));
            }
        }
    };
}

// update search criteria by toggling filter value
export function toggleSearchFilter(uiFilterName, uiFilterOption) {
    return (dispatch, getState) => {
        const filterOptions = getState().search.uiFilters[uiFilterName] || {};

        const removeFilter = !!filterOptions[uiFilterOption.key];

        if (removeFilter) {
            return dispatch(removeSearchFilter(uiFilterName, uiFilterOption));
        }

        return dispatch(addSearchFilter(uiFilterName, uiFilterOption));
    };
}

// reset search filters (other than what/where/lat/lng)
export function resetSearchFiltersCampsites() {
    return {
        type: types.RESET_SEARCH_FILTERS_CAMPSITES,
    };
}

// Fetch suggestions, using search service API
export function fetchSuggestions(prefix, inventoryTypes, contentTypes, geocoder = true) {
    return (dispatch) => {
        const params = {
            q: prefix,
            geocoder,
            type: contentTypes,
            inventory_type: inventoryTypes,
        };
        return axios
            .get(utils.urlWithParams(`${process.env.API}/search/suggest`, params), {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((success) => {
                dispatch({
                    type: types.GET_SEARCH_SUGGESTIONS,
                    suggestions: success.data.suggestions,
                    inventory_suggestions: success.data.inventory_suggestions,
                    content_suggestions: success.data.content_suggestions,
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_SEARCH_SUGGESTIONS_ERROR,
                    err: `${error}`,
                });
            });
    };
}

function buildTourTimesFilter(uiFilter) {
    const filterValues = [];

    if (uiFilter && uiFilter.tourTimes) {
        const tourTimesFilter = uiFilter.tourTimes;

        for (const key in tourTimesFilter) {
            if (Object.hasOwn(tourTimesFilter, key)) {
                const item = tourTimesFilter[key];
                if (Array.isArray(item.filters)) {
                    item.filters.forEach((filter) => {
                        if (Object.hasOwn(filter, 'filterValue')) {
                            filterValues.push(filter.filterValue);
                        }
                    });
                }
            }
        }
    }

    return filterValues;
}

// build filters from search state
export function buildSearchFilters(baseFilters, UIFilters) {
    const uiFilters = cloneDeep(UIFilters);
    const ret = [];
    // append raw base filters
    if (baseFilters) {
        baseFilters.forEach((filter) => {
            filter.values.forEach((value) => {
                const parts = value.value.split(',');
                parts.forEach((part) => {
                    if (part.startsWith('>') || part.startsWith('<')) {
                        // range filters
                        ret[ret.length] = `${filter.attribute}${part}`;
                    } else {
                        // term filters
                        ret[ret.length] = `${filter.attribute}:${part}`;
                    }
                });
            });
        });
    }

    if (uiFilters && uiFilters.tourTimes) {
        delete uiFilters.tourTimes;
    }

    // convert and append uiFilters to filters
    if (uiFilters) {
        // only using specific filter instead of general
        if (
            !isEmpty(uiFilters.campsiteElectricityHookup) &&
            uiFilters.campsiteAmenities &&
            !isEmpty(uiFilters.campsiteAmenities['campsite-amenities-electricity-hookup'])
        ) {
            delete uiFilters.campsiteAmenities['campsite-amenities-electricity-hookup'];
        }
        for (const propName in uiFilters) {
            if (isObject(uiFilters[propName]) && !isEmpty(uiFilters[propName])) {
                for (const key in uiFilters[propName]) {
                    if (key in uiFilters[propName]) {
                        const uiFilter = uiFilters[propName][key];
                        uiFilter.filters.forEach((filter) => {
                            let alreadySet = false;
                            baseFilters.forEach((f) => {
                                if (filter.attribute === f.attribute) {
                                    alreadySet = true;
                                }
                            });
                            if (!alreadySet) {
                                const parts = filter.filterValue.split(',');
                                parts.forEach((part) => {
                                    if (part.startsWith('>') || part.startsWith('<')) {
                                        // range filters
                                        ret[ret.length] = `${filter.attribute}${part}`;
                                    } else {
                                        // term filters
                                        ret[ret.length] = `${filter.attribute}:${part}`;
                                    }
                                });
                            }
                        });
                    }
                }
            }
        }
    }

    return ret;
}

// helper method to construct search GET uri (the uri is the same for search service call as well as for front end)
function buildSearchServiceRequest(baseUri, state, skipStartParam) {
    // build filters
    const filters = buildSearchFilters(state.search.filters, state.search.uiFilters);
    const tourTimes = buildTourTimesFilter(state.search.uiFilters);
    if (state.search.parent_asset_id) {
        filters[filters.length] = `parent_asset_id:${state.search.parent_asset_id}`;
    }
    if (state.search.org_id) {
        filters[filters.length] = `org_id:${state.search.org_id}`;
    }

    // build aggregations
    const aggs = [];
    (state.search.aggregations ?? []).forEach((agg) => {
        aggs[aggs.length] = agg;
    });

    // create request params
    const params = {
        q: state.search.what,
        entity_id: state.search.entity_id,
        entity_type: state.search.entity_type,
        exact: state.search.exact,
        lat: state.search.lat,
        lng: state.search.lng,
        radius: state.search.radius,
        location: state.search.location,
        lat_sw: state.search.lat_sw,
        lng_sw: state.search.lng_sw,
        lat_ne: state.search.lat_ne,
        lng_ne: state.search.lng_ne,
        size: state.search.size != null ? state.search.size : 20,
        agg: aggs,
        fq: filters,
        group_agg: state.search.group_agg,
        fg: state.search.fg,
        tour_times: tourTimes,
        sort: state.search.sort,
    };

    if (state.search.avail_nights) {
        params.avail_nights = state.search.avail_nights;
    }

    if (state.search.avail_months) {
        params.avail_months = state.search.avail_months;
    }

    if (state.search.flex_start_range) {
        params.flex_start_range = state.search.flex_start_range;
    }

    if (state.search.flex_end_range) {
        params.flex_end_range = state.search.flex_end_range;
    }

    if (!skipStartParam) {
        params.start = state.search.start !== null ? state.search.start : 0;
    }

    const includeUnavailable = get(state.search, 'include_unavailable', true);
    const includeNotReservable = get(state.search, 'include_notreservable', true);
    const includePartiallyAvailable = get(
        state.search,
        'include_partially_available',
        true
    );

    if (
        includeNotReservable === false ||
        includeUnavailable === false ||
        includePartiallyAvailable === false
    ) {
        params.fq[params.fq.length] = 'reservable:1';
    }

    if (state.search.checkin_time && state.search.checkout_time) {
        params.start_date = searchDate(state.search.checkin_time)
            .utc()
            .startOf('day')
            .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
        params.end_date = searchDate(state.search.checkout_time)
            .utc()
            .startOf('day')
            .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    }

    if (includeNotReservable === false) {
        params.include_notreservable = includeNotReservable;
    }

    if (includeUnavailable === false) {
        params.include_unavailable = includeUnavailable;
    }

    if (includePartiallyAvailable === false) {
        params.include_partially_available = includePartiallyAvailable;
    }

    return utils.urlWithParams(baseUri, params);
}

function parseFgForQuery(props) {
    const fg = get(props, 'search.fg', []);
    const fgParams = {};
    if (fg.length > 0) {
        const getPathFg = (filters, key) => filters.find((item) => item.key === key);
        fg.forEach((v) => {
            const campingType = getPathFg(CAMPING_TYPES, v);
            if (campingType) {
                fgParams.campsite_type = [
                    ...get(fgParams, 'campsite_type', []),
                    campingType.value.toLowerCase(),
                ];
            }
            const electricityType = getPathFg(ELECTRIC_TYPE, v);
            if (electricityType) {
                fgParams.electricity = [
                    ...get(fgParams, 'electricity', []),
                    electricityType.value.toLowerCase(),
                ];
            }
            const amenitiesType = getPathFg(AMENITIES_TYPE, v);
            if (amenitiesType) {
                fgParams.amenities = [
                    ...get(fgParams, 'amenities', []),
                    amenitiesType.value.toLowerCase(),
                ];
            }
            const electricalHookup = getPathFg(SEARCH_FILTER_CAMPSITE_ELECTRICAL, v);
            if (electricalHookup) {
                fgParams.electrical_hookup = [
                    ...get(fgParams, 'electrical_hookup', []),
                    electricalHookup.value.toLowerCase(),
                ];
            }
            const keyCharacteristics = getPathFg(KEY_CHARACTERISTICS_TYPE, v);
            if (keyCharacteristics) {
                fgParams.key_characteristics = [
                    ...get(fgParams, 'key_characteristics', []),
                    keyCharacteristics.value.toLowerCase(),
                ];
            }
            const accessType = getPathFg(ACCESS_TYPE, v);
            if (accessType) {
                fgParams.access = [...get(fgParams, 'access', []), accessType.value];
            }
            const specialSiteType = getPathFg(SPECIAL_SITE_TYPE, v);
            if (specialSiteType) {
                fgParams.special_site = [
                    ...get(fgParams, 'special_site', []),
                    specialSiteType.value.toLowerCase(),
                ];
            }
            const inventory = getPathFg(INVENTORY_TYPES_FILTERS, v);
            if (inventory) {
                fgParams.inventory_type = [
                    ...get(fgParams, 'inventory_type', []),
                    inventory.value.toLowerCase(),
                ];
            }
            const firstComeFirstServe = getPathFg(FCFS_TYPE, v);
            if (firstComeFirstServe) {
                fgParams.fcfs = [
                    ...get(fgParams, 'fcfs', []),
                    firstComeFirstServe.value.toLowerCase(),
                ];
            }
            if (v.includes(VEHICLE_LENGTH_TYPE.key)) {
                const value = v.split(':');
                fgParams[VEHICLE_LENGTH_TYPE.key] = value[1];
            }
            if (v.includes(OCCUPANTS_TYPE.key)) {
                const value = v.split(':');
                fgParams[OCCUPANTS_TYPE.key] = value[1];
            }
            if (v === CAMPSITE_DAYUSE_TYPE) {
                fgParams.booking_type = [
                    ...get(fgParams, 'booking_type', []),
                    INVENTORY_TYPES_DAY_USE,
                ];
            }
            if (v === CAMPSITE_CAMPING_TYPE) {
                fgParams.booking_type = [
                    ...get(fgParams, 'booking_type', []),
                    INVENTORY_TYPES_CAMPING,
                ];
            }
        });
    }
    return fgParams;
}

function generateUiFiltersForQueryArray(value, key) {
    const params = {};
    Object.values(value)?.forEach((v) => {
        const current = get(params, key, []);
        params[key] = [...current, v.value?.toLowerCase()];
    });
    return params;
}

function parseUiFiltersForQuery(props) {
    let uiFiltersParams = {};
    const uiFilters = get(props, 'search.uiFilters', {});
    Object.entries(uiFilters).forEach((item) => {
        const [key, value] = item;
        if (typeof value === 'object' && !isEmpty(value)) {
            const current = types.uiFiltersGlossary.find((v) => v.value === key);
            if (key !== 'activities' && current) {
                uiFiltersParams = {
                    ...uiFiltersParams,
                    ...generateUiFiltersForQueryArray(value, current.key),
                };
            } else if (key === 'activities') {
                uiFiltersParams = {
                    ...uiFiltersParams,
                    ...generateUiFiltersForQueryArray(value, 'activity'),
                };
            }
        }
    });
    return uiFiltersParams;
}

// helper method to construct the user-facing URL that reflects the redux store state
export function buildSearchNavUri(baseUri, props) {
    let activitiesParam = null;
    const activitiesFilters = get(props, 'search.uiFilters.activities', {});
    const pActivity = get(props, 'search.activity');

    if (
        pActivity &&
        !isEmpty(activitiesFilters) &&
        lodashSize(isArray(pActivity) ? pActivity : [pActivity]) ===
            lodashSize(activitiesFilters)
    ) {
        activitiesParam = pActivity;
    }

    let campsiteEquipmentParam = null;
    const campsiteEquipmentFilters = get(props, 'search.uiFilters.campsiteEquipment', {});
    const pCampsiteEquipment = get(props, 'search.campsite_equipment');
    if (
        pCampsiteEquipment &&
        !isEmpty(campsiteEquipmentFilters) &&
        lodashSize(
            isArray(pCampsiteEquipment) ? pCampsiteEquipment : [pCampsiteEquipment]
        ) === lodashSize(campsiteEquipmentFilters)
    ) {
        campsiteEquipmentParam = pCampsiteEquipment;
    }

    let campsiteAmenitiesParam = null;
    const campsiteAmenitiesFilters = get(props, 'search.uiFilters.campsiteAmenities', {});
    const pCampsiteAmenities = get(props, 'search.campsite_amenity');
    if (
        pCampsiteAmenities &&
        !isEmpty(campsiteAmenitiesFilters) &&
        lodashSize(
            isArray(pCampsiteAmenities) ? pCampsiteAmenities : [pCampsiteAmenities]
        ) === lodashSize(campsiteAmenitiesFilters)
    ) {
        campsiteAmenitiesParam = pCampsiteAmenities;
    }

    let campsiteTypeParam = null;
    const campsiteTypeFilters = get(props, 'search.uiFilters.campsiteTypes', {});
    const pCampsiteType = get(props, 'search.campsite_type');
    if (
        pCampsiteType &&
        !isEmpty(campsiteTypeFilters) &&
        lodashSize(isArray(pCampsiteType) ? pCampsiteType : [pCampsiteType]) ===
            lodashSize(campsiteTypeFilters)
    ) {
        campsiteTypeParam = pCampsiteType;
    }
    const uiFilterParams = parseUiFiltersForQuery(props);
    const fgParams = parseFgForQuery(props);

    // Build activity_tags URL param from uiFilters.activityTags if present,
    // otherwise fall back to state.search.filters for backward compatibility
    //
    // activity_tags in the URL are a single string value that is a comma separated list,
    // e.g. ?activity_tags=hiking,fishing,camping
    const uiActivityTags = props.search.uiFilters?.activityTags || {};
    const uiActivityTagValues = Object.values(uiActivityTags).map(
        (tag) => tag.filters?.[0]?.filterValue
    );
    const activityTagsValue =
        uiActivityTagValues.length > 0
            ? uiActivityTagValues.join(',')
            : props.search.filters
                  ?.filter((item) => item.attribute === 'activity_tags')
                  ?.map((item) => item.values?.map((v) => v.value))?.[0];

    const params = {
        q: props.search.what,
        entity_id: props.search.entity_id,
        entity_type: props.search.entity_type,
        lat: props.search.lat,
        lng: props.search.lng,
        radius: props.search.radius,
        lat_sw: props.search.lat_sw,
        lng_sw: props.search.lng_sw,
        lat_ne: props.search.lat_ne,
        lng_ne: props.search.lng_ne,
        start: props.search.start,
        size: props.search.size,
        inventory_type: props.search.inventory_type,
        activity: activitiesParam,
        campsite_equipment: campsiteEquipmentParam,
        campsite_amenity: campsiteAmenitiesParam,
        campsite_type: campsiteTypeParam,
        parent_asset_id: props.search.parent_asset_id,
        org_id: props.search.org_id,
        ...uiFilterParams,
        ...fgParams,
        sort: props.search.sort,
        checkin: props.search.checkin_time,
        checkout: props.search.checkout_time,
        utm_source: props.search.utm_source,
        utm_medium: props.search.utm_medium,
        utm_campaign: props.search.utm_campaign,
        utm_content: props.search.utm_content,
        avail_nights: props.search.avail_nights,
        avail_months: props.search.avail_months,
        flex_start_range: props.search.flex_start_range,
        flex_end_range: props.search.flex_end_range,
        activity_tags: activityTagsValue,
    };

    return utils.urlWithParams(baseUri, params);
}

// URL params that change how the results are shown, not which results match
const NON_CRITERIA_PARAMS = [
    'sort',
    'start',
    'size',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
];

/**
 * Identifies the results a search state matches: two states with the same key match
 * the same results, whatever their sort, page, or tracking params
 * @param {object} search the `search` state
 * @returns {string}
 */
export function buildSearchCriteriaKey(search) {
    const params = getRequestParams(buildSearchNavUri('', { search }));
    return [...params.entries()]
        .filter(([name]) => !NON_CRITERIA_PARAMS.includes(name))
        .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
        .sort()
        .join('&');
}

/**
 * The query of the AI search the latest search request comes from. Sending a
 * search for any other criteria, wherever the change to the criteria came from,
 * ends the AI search for good.
 * @param {{ search: object, searchResults: object }} state the `search` and
 * `searchResults` state
 * @returns {string}
 */
export function getActiveAiQuery({ search, searchResults }) {
    const aiSearch = search?.aiSearch;
    if (
        !aiSearch?.query ||
        aiSearch.criteriaKey !== searchResults?.requestedCriteriaKey
    ) {
        return '';
    }
    return aiSearch.query;
}

/**
 * Whether the search criteria are still the ones the AI search built
 * @param {object} search the `search` state
 * @returns {boolean}
 */
function hasAiSearchCriteria(search) {
    const aiSearch = search?.aiSearch;
    return (
        Boolean(aiSearch?.query) &&
        aiSearch.criteriaKey === buildSearchCriteriaKey(search)
    );
}

/**
 * Records the current search criteria as the ones an AI search built for `query`
 * @param {string} query what the user asked the AI search for
 */
function setAiSearch(query) {
    return (dispatch, getState) =>
        dispatch(
            updateSearchCriteria('aiSearch', {
                query,
                criteriaKey: buildSearchCriteriaKey(getState().search),
            })
        );
}

/**
 * Replaces the search criteria with the ones an AI search built
 * @param {string} query what the user asked the AI search for
 * @param {object} criterias the search criteria the AI built from it
 */
export function startAiSearch(query, criterias) {
    return (dispatch) => {
        dispatch(updateSearchCriterias(criterias));
        dispatch(setAiSearch(query));
    };
}

/**
 * Updates search criteria the app fills in for the user, like their location. The
 * user didn't change what they searched for, so the criteria stay the AI search's.
 * @param {object} params the search criteria to update
 */
function fillInSearchCriterias(params) {
    return (dispatch, getState) => {
        const { search } = getState();
        const isAiSearch = hasAiSearchCriteria(search);
        dispatch(updateSearchCriterias(params));
        if (isAiSearch) {
            dispatch(setAiSearch(search.aiSearch.query));
        }
    };
}

/**
 * URL of the request for a group of aggregation counts, e.g. the camping filters
 * @param {{ search: object }} appState the `search` state
 * @param {string[]} groupName aggregation groups to count
 * @param {boolean} [isCampsites] whether to count campsites instead of facilities
 * @param {object[] | null} [additionalFilters] raw filters to add to the request
 * @param {boolean} [omitFg] whether to leave the selected filter groups out
 * @returns {string}
 */
function buildFilterGroupAggregationsRequestUrl(
    appState,
    groupName,
    isCampsites,
    additionalFilters,
    omitFg
) {
    const state = cloneState(appState);
    const params = { search: { ...state.search } };

    params.search.group_agg = groupName;
    params.search.size = 0;
    params.search.sort = null;
    // use same radius as the default search query so aggregations counts match
    params.search.radius = DEFAULT_RADIUS;

    let searchUrl = `${process.env.API}/search`;
    if (additionalFilters) {
        additionalFilters.forEach((filter) => {
            params.search.filters[params.search.filters.length] = filter;
        });
    }

    const legacyKeys = [
        'campsiteTypes',
        'campsiteAmenities',
        'campsiteElectricityHookup',
        'campsiteEquipment',
    ];

    const uiFilters = {};
    if (params.search?.uiFilters) {
        for (const propName in params.search?.uiFilters) {
            if (legacyKeys.indexOf(propName) === -1) {
                uiFilters[propName] = params.search.uiFilters[propName];
            }
        }
    }

    params.search.uiFilters = uiFilters;

    if (isCampsites) {
        const parsedFg = parseCampsiteFilters(get(params, 'search.fg', []));
        searchUrl = `${process.env.API}/search/campsites`;
        params.search.what = null;
        params.search.headerTextQuery = null;
        params.search.q = null;
        params.search.fq = [``];
        params.search.checkin_time = null;
        params.search.checkout_time = null;
        params.search.lat = null;
        params.search.lng = null;
        params.search.radius = null;
        params.search.location = null;
        params.search.lat_sw = null;
        params.search.lng_sw = null;
        params.search.lat_ne = null;
        params.search.lng_ne = null;
        params.search.fg = parsedFg;
    }

    if (omitFg) {
        params.search.fg = [];
    }

    return buildSearchServiceRequest(searchUrl, params);
}

// fetch group aggregations for attributes of a UI Filter
export function fetchFilterGroupAggregations(
    groupName,
    invType,
    isCampsites,
    additionalFilters,
    omitFg
) {
    return (dispatch, getState) => {
        const searchServiceUrl = buildFilterGroupAggregationsRequestUrl(
            getState(),
            groupName,
            isCampsites,
            additionalFilters,
            omitFg
        );
        const initialAggregations = [{ name: 'camping', count: 0 }];

        return fetch(searchServiceUrl, { credentials: 'include' })
            .then(status)
            .then(parseJson)
            .then((json) => {
                if (json.err) {
                    dispatch(searchFailed('Error search results'));
                } else {
                    let payload = {};
                    const result = get(
                        json,
                        'aggregations["group-aggregations"]',
                        initialAggregations
                    );
                    result.forEach((item) => {
                        payload = { ...payload, [item.name]: item.count };
                    });

                    dispatch({
                        type: types.GET_SEARCH_FILTER_GROUP_AGGREGATIONS,
                        groupName: invType,
                        aggregations: payload,
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_SEARCH_FILTER_GROUP_AGGREGATIONS,
                    groupName: invType,
                    aggregations: initialAggregations,
                });
                dispatch(searchFailed(`Error fetching data: ${error}`));
            });
    };
}

export function fetchCampsitesSearchFiltersAggregations(campgroundId, dispatch) {
    dispatch(updateSearchCriterias({ filters: [] }));
    dispatch(
        fetchFilterGroupAggregations(
            [
                'camping-type',
                'electricity',
                'key-characteristics',
                'access',
                'special-site-type',
                'amenities',
                'booking-types',
            ],
            'camping',
            true,
            [
                {
                    attribute: 'asset_id',
                    values: [{ label: '', value: campgroundId }],
                },
            ]
        )
    );
}

/**
 * Whether a keyword asks for results near the user, e.g. "camping near me"
 * @param {string | undefined | null} what the search keyword
 * @returns {boolean}
 */
function isNearbyKeyword(what) {
    if (!what) {
        return false;
    }
    const q = what.toLowerCase();
    return nearByDictionary.some((word) => q.indexOf(word) >= 0);
}

/**
 * Where a search looks for results, before any lookup of the user's position.
 * A search with no point and no keyword (or a "near me" keyword) has no
 * location, unless the user's position should be looked up first.
 * @param {object} search the `search` state
 * @returns {{ lat: unknown, lng: unknown, radius: unknown, location: unknown, shouldLocateUser: boolean }}
 */
export function getSearchLocation(search) {
    const hasNoPoint =
        isBlank(search.lat) && (isBlank(search.what) || isNearbyKeyword(search.what));
    const shouldLocateUser = hasNoPoint && search.use_location === true;

    if (hasNoPoint && !shouldLocateUser) {
        return { lat: null, lng: null, radius: null, location: null, shouldLocateUser };
    }

    return {
        lat: search.lat,
        lng: search.lng,
        radius: search.radius || DEFAULT_RADIUS,
        location: search.location,
        shouldLocateUser,
    };
}

/**
 * URL of the search results request for a search state and the location to
 * search around. Asks for the first page of up to `MAX_SEARCH_RESULTS` results.
 * @param {object} search the `search` state
 * @param {{ lat: unknown, lng: unknown, radius: unknown, location: unknown }} searchLocation
 * @returns {string}
 */
export function buildSearchResultsRequestUrl(search, { lat, lng, radius, location }) {
    const filters = Array.isArray(search.filters) ? [...search.filters] : [];

    // Exclude individual tours from results unless there's already a conflicting filter
    if (!filters.some((f) => f.attribute === '-entity_type')) {
        filters.push({
            attribute: '-entity_type',
            values: [{ value: '(tour OR timedentry_tour)' }],
        });
    }

    /** ************************************************************
     * SWITCHED TO USING GEO HERE ...
     * was /search before, which works but the response size is much larger
     * The trade-off is that the geo response does not contain as much pricing data
     * as /search does. Prices are used on the cards; unclear how valuable it is
     */
    return buildSearchServiceRequest(`${process.env.API}/search/geo`, {
        search: {
            ...search,
            lat,
            lng,
            radius,
            location,
            filters,
            size: MAX_SEARCH_RESULTS,
            start: 0,
            sort: search.sort || 'score',
        },
    });
}

/**
 * Query params of the search results request `fetchSearchResults` sends for a
 * search state, without looking up the user's position
 * @param {object} search the `search` state
 * @returns {URLSearchParams}
 */
export function buildSearchResultsParams(search) {
    return getRequestParams(
        buildSearchResultsRequestUrl(search, getSearchLocation(search))
    );
}

/**
 * Query params of a request URL
 * @param {string} url
 * @returns {URLSearchParams}
 */
function getRequestParams(url) {
    const queryStart = url.indexOf('?');
    return new URLSearchParams(queryStart === -1 ? '' : url.slice(queryStart + 1));
}

// Id of the latest search. Responses to older searches are dropped, so a slow
// search can't overwrite the results of a newer one
let latestSearchRequestId = 0;

// fetch search results using current search criteria state
export function fetchSearchResults(options = {}) {
    return (dispatch, getState) => {
        latestSearchRequestId += 1;
        const requestId = latestSearchRequestId;

        // geocode place if it's selected but lat/lng are not available
        const state = { ...getState() };
        const searchLocation = getSearchLocation(state.search);
        let { lat, lng, location } = searchLocation;
        const { radius } = searchLocation;
        // How the search location was resolved ('navigator' | 'ip' | 'cache' | null).
        // Threaded through the background re-dispatch (see upgradeToNavigatorInBackground)
        // so the results heading can reflect a browser-derived "nearby" search.
        let locationSource = options?.locationSource ?? null;

        // geocode client request on client side, if needed
        let $geocode = new Promise((resolve) => {
            dispatch({ type: types.START_GET_SEARCH_RESULTS });
            resolve();
        });

        const geocodeUserLocation = searchLocation.shouldLocateUser;

        // geocode current user position if no other location specific parameters are defined in search request
        if (geocodeUserLocation) {
            $geocode = new Promise((resolve) => {
                dispatch({ type: types.START_GET_SEARCH_RESULTS });

                // Apply a resolved position to the search criteria.
                // `position.location` is a display string (e.g. "Boise, Idaho") or null.
                function onPositionSuccess(position) {
                    if (position) {
                        location = position.location || null;
                        locationSource = position.source ?? null;
                        lat = position.latitude;
                        lng = position.longitude;

                        // Search within DEFAULT_RADIUS (km) of the user's location.
                        // 1° latitude ≈ 111 km; 1° longitude ≈ 111 km × cos(latitude).
                        const KM_PER_DEGREE = 111;
                        const latOffset = DEFAULT_RADIUS / KM_PER_DEGREE;
                        const lngOffset =
                            DEFAULT_RADIUS /
                            (KM_PER_DEGREE * Math.cos((lat * Math.PI) / 180));

                        const latNe = lat + latOffset;
                        const lngNe = lng + lngOffset;
                        const latSw = lat - latOffset;
                        const lngSw = lng - lngOffset;
                        dispatch(
                            fillInSearchCriterias({
                                lat,
                                lng,
                                location,
                                radius: null,
                                placeid: null,
                                placename: null,
                                where: null,
                                latNe,
                                lngNe,
                                latSw,
                                lngSw,
                            })
                        );
                    }
                }

                // Normalize raw coordinates (browser or geoip) into a UserLocation,
                // clamping out-of-US coordinates to a default and deriving a display string.
                function toUserLocation(raw, source) {
                    const {
                        latitude,
                        longitude,
                        location: locationName,
                        isInUS,
                    } = getLocationWithFallback(raw);
                    return {
                        latitude,
                        longitude,
                        location: locationName || null,
                        source,
                        isInUS,
                    };
                }

                // Apply a resolved location and persist it to the shared cache.
                function applyLocation(userLocation) {
                    // Never let a slow/stale geoip result clobber a more accurate
                    // browser location that a concurrent invocation may have already
                    // cached (see race analysis: overlapping fetchSearchResults calls).
                    const current = getCachedLocation();
                    if (userLocation.source === 'ip' && current?.source === 'navigator') {
                        return;
                    }
                    setCachedLocation(userLocation);
                    onPositionSuccess(userLocation);
                }

                // Browser Geolocation, wrapped as a promise. Rejects if unsupported.
                function getNavigatorLocation() {
                    return new Promise((navResolve, navReject) => {
                        if (!navigator.geolocation) {
                            navReject();
                            return;
                        }
                        navigator.geolocation.getCurrentPosition(
                            (pos) =>
                                navResolve({
                                    latitude: pos.coords.latitude,
                                    longitude: pos.coords.longitude,
                                }),
                            () => navReject(),
                            {
                                // optimization params
                                enableHighAccuracy: false,
                                maximumAge: Infinity,
                                timeout: 10000,
                            }
                        );
                    });
                }

                // IP-based geolocation. Resolves the raw geoip json, or null on failure.
                function getGeoIpLocation() {
                    return fetch(`${process.env.API}/recommendation/geoip`)
                        .then(status)
                        .then(parseJson)
                        .then((json) => {
                            if (
                                json.latitude &&
                                json.longitude &&
                                json.latitude !== 0 &&
                                json.longitude !== 0
                            ) {
                                return json;
                            }
                            return null;
                        })
                        .catch(() => null);
                }

                // Current geolocation permission state.
                function getGeolocationPermissionState() {
                    if (!navigator.permissions || !navigator.permissions.query) {
                        return Promise.resolve('unsupported');
                    }
                    return navigator.permissions
                        .query({ name: 'geolocation' })
                        .then((permissionStatus) => permissionStatus.state)
                        .catch(() => 'unsupported');
                }

                // Resolve the search using geoip (fast, no permission needed).
                function resolveWithGeoIp() {
                    return getGeoIpLocation().then((json) => {
                        if (json) {
                            applyLocation(toUserLocation(json, 'ip'));
                        }
                        resolve();
                    });
                }

                // Fetch the (more accurate) browser location without blocking, then
                // overwrite the cached location and re-run the search on success.
                function upgradeToNavigatorInBackground() {
                    getNavigatorLocation()
                        .then((coords) => {
                            applyLocation(toUserLocation(coords, 'navigator'));
                            dispatch(fetchSearchResults({ locationSource: 'navigator' }));
                        })
                        .catch(() => {
                            // Denied or timed out; keep whatever location we have.
                        });
                }

                // 1) First choice: a valid, non-expired shared cache entry.
                const cachedLocation = getCachedLocation();
                if (cachedLocation) {
                    onPositionSuccess(cachedLocation);
                    resolve();

                    // If the cached value came from geoip and the user has since
                    // granted location permission, silently upgrade to the more
                    // accurate browser location (avoids getting stuck on geoip).
                    if (cachedLocation.source !== 'navigator') {
                        getGeolocationPermissionState().then((permissionState) => {
                            if (permissionState === 'granted') {
                                upgradeToNavigatorInBackground();
                            }
                        });
                    }
                    return;
                }

                // 2) Prefer the (more accurate) browser location, but never block
                //    the search on the permission prompt.
                getGeolocationPermissionState().then((permissionState) => {
                    if (permissionState === 'granted') {
                        // Already granted: browser location resolves quickly, so
                        // wait for it and skip geoip (no flash / no double-search).
                        getNavigatorLocation()
                            .then((coords) => {
                                applyLocation(toUserLocation(coords, 'navigator'));
                                resolve();
                            })
                            .catch(() => {
                                // Granted but the lookup failed; fall back to geoip.
                                resolveWithGeoIp();
                            });
                        return;
                    }

                    if (permissionState === 'prompt') {
                        // Undecided: show geoip results immediately (don't block on
                        // the prompt), then upgrade to browser location in the
                        // background and re-run the search if the user grants access.
                        resolveWithGeoIp().then(() => {
                            upgradeToNavigatorInBackground();
                        });
                        return;
                    }

                    // 'denied' | 'unsupported': geoip only.
                    resolveWithGeoIp();
                });
            });
        }

        return $geocode.then(() => {
            // Originally we were updating the redux store to blow away any filter values.
            // At a minimum, we should be updating it with these new filter values
            // But updating the filters at fetch time is weird, so
            // temporarily disabling this and we'll see how it goes
            // Initial (pre Aug 27, 2025):
            // dispatch(updateSearchCriteria('filters', []));
            // Probably better, but not better than just not doing it:
            // dispatch(updateSearchCriteria('filters', filters));

            if (requestId === latestSearchRequestId) {
                dispatch({
                    type: types.SEND_SEARCH_RESULTS_REQUEST,
                    criteriaKey: buildSearchCriteriaKey(getState().search),
                });
            }

            const searchServiceUrl = buildSearchResultsRequestUrl(state.search, {
                lat,
                lng,
                radius,
                location,
            });

            fetch(searchServiceUrl, { credentials: 'include' })
                .then(status)
                .then(parseJson)
                .then((json) => {
                    if (requestId !== latestSearchRequestId) {
                        return;
                    }

                    if (json.err) {
                        dispatch(searchFailed('Error search results'));
                    } else {
                        dispatch({
                            type: types.GET_SEARCH_RESULTS,
                            payload: json,
                            isBrowserLocation: locationSource === 'navigator',
                        });

                        const uri = buildSearchNavUri('/search', state);
                        window.history.replaceState(window.history.state, '', uri);
                    }

                    dispatch({ type: types.END_GET_SEARCH_RESULTS });
                })
                .catch((error) => {
                    if (requestId !== latestSearchRequestId) {
                        return;
                    }

                    dispatch({ type: types.END_GET_SEARCH_RESULTS });
                    dispatch(searchFailed(`Error fetching data: ${error}`));
                });
        });
    };
}

// show search results in response to a user action using current search state
export function navigateToSearchResults() {
    return (dispatch, getState) => {
        return dispatch(fetchSearchResults()).then(() => {
            const uri = buildSearchNavUri('/search', getState());
            if (window.location?.pathname.startsWith('/search')) {
                window.history.replaceState(window.history.state, '', uri);
            } else {
                window.location?.assign(uri);
            }
        });
    };
}

/**
 * `uiFilters.activities` for the activities in a URL, dropping unknown activities
 * @param {string[]} activities activity labels, e.g. `historic & cultural site`
 * @returns {Record<string, import('../utils/filters').UiFilterOption>}
 */
export function setActivityFilters(activities) {
    let filters = {};
    if (activities) {
        const values = [...activities];
        values.forEach((name) => {
            const option = name ? findActivityOption(name) : undefined;
            if (option) {
                filters = { ...filters, [option.key]: toActivitySelection(option) };
            }
        });
    }
    return filters;
}

// add recreational agency filter to search criteria
export function setAgencyFilters(agencies) {
    let filters = {};
    if (agencies) {
        const values = [...agencies];
        values.forEach((name) => {
            if (name) {
                const filter = types.SEARCH_FILTER_AGENCIES_VALUES.find(
                    (item) => !isEmpty(item.filters.filter((v) => v.filterValue === name))
                );
                if (filter) {
                    filters = { ...filters, [filter.key]: filter };
                }
            }
        });
    }
    return filters;
}

export function setGlobalUiFilter(queryParams, uiFilters) {
    const params = {};
    const clearFilters = isArray(queryParams) ? queryParams : [queryParams];
    clearFilters?.forEach((item) => {
        const current = uiFilters.find(
            (v) => v?.value?.toLowerCase() === item?.toLowerCase()
        );
        if (current) {
            params[current.key] = current;
        }
    });
    return params;
}

function parseUiFiltersFromQuery(props, uiFilters) {
    let uiFiltersParams = { ...uiFilters };
    if (props.activity) {
        const activities = isArray(props.activity) ? props.activity : [props.activity];
        uiFiltersParams = {
            ...uiFiltersParams,
            activities: setActivityFilters(activities),
        };
    }
    if (props.org_id) {
        const agencies = isArray(props.org_id) ? props.org_id : [props.org_id];
        uiFiltersParams = {
            ...uiFiltersParams,
            agencies: setAgencyFilters(agencies),
        };
    }
    types.uiFiltersGlossary.forEach((item) => {
        if (props[item.key]) {
            uiFiltersParams = {
                ...uiFiltersParams,
                [item.value]: setGlobalUiFilter(props[item.key], item.filters),
            };
        }
    });
    return uiFiltersParams;
}

// rebuild store state from query params
export function parseQueryParams(urlParams, dispatch, state) {
    const queryParams = urlParams;
    let search = { ...state.search };
    let changed = false;
    const params = {};

    if (queryParams) {
        if (queryParams.clear === 'true') {
            search = types.SEARCH_INITIAL_STATE;
            params.location = null;
        }
        if (queryParams.use_location === 'false') {
            params.use_location = false;
            params.location = null;
        } else {
            params.use_location = true;
        }
        // handle query text param
        if (
            search.what !== queryParams.q ||
            queryParams.q === 'camping' ||
            queryParams.q === 'venues'
        ) {
            if (queryParams?.q?.toLowerCase() === 'camping') {
                // if someone just searching for camping, convert criteria to filter by camping inventory and remove text
                search.what = '';
                params.what = '';
                queryParams.inventory_type = ['camping'];
            } else if (queryParams?.q?.toLowerCase() === 'venues') {
                search.what = '';
                params.what = '';
                queryParams.inventory_type = ['venuereservations'];
            } else {
                search.what = queryParams.q;
                params.what = queryParams.q;
                params.headerTextQuery = queryParams.q;
            }
        }
        // prioritize the value in the query over the user location
        if (queryParams.q && window.location) {
            params.location = null;
        }
        if (search.radius !== queryParams.radius) {
            search.radius = queryParams.radius;
            params.radius = queryParams.radius;
        }
        if (search.lat !== queryParams.lat) {
            search.lat = queryParams.lat;
            params.lat = queryParams.lat;
        }
        if (search.lng !== queryParams.lng) {
            search.lng = queryParams.lng;
            params.lng = queryParams.lng;
        }
        if (search.lat_sw !== queryParams.lat_sw) {
            search.lat_sw = queryParams.lat_sw;
            params.lat_sw = queryParams.lat_sw;
        }
        if (search.lng_sw !== queryParams.lng_sw) {
            search.lng_sw = queryParams.lng_sw;
            params.lng_sw = queryParams.lng_sw;
        }
        if (search.lat_ne !== queryParams.lat_ne) {
            search.lat_ne = queryParams.lat_ne;
            params.lat_ne = queryParams.lat_ne;
        }
        if (search.lng_ne !== queryParams.lng_ne) {
            search.lng_ne = queryParams.lng_ne;
            params.lng_ne = queryParams.lng_ne;
        }
        if (queryParams.parent_asset_id) {
            search.parent_asset_id = queryParams.parent_asset_id;
            params.parent_asset_id = queryParams.parent_asset_id;
        } else {
            search.parent_asset_id = null;
            params.parent_asset_id = null;
        }
        if (queryParams.org_id) {
            search.org_id = queryParams.org_id;
            params.org_id = queryParams.org_id;
        } else {
            search.org_id = null;
            params.org_id = null;
        }
        if (search.start !== queryParams.start) {
            let start = null;
            if (queryParams.start) {
                start = Number(queryParams.start);
            }
            if (start === 0) {
                start = null;
            }
            search.start = start;
            params.start = start;
        }
        if (search.size !== queryParams.size) {
            let size = null;
            if (queryParams.size) {
                size = Number(queryParams.size);
            }
            if (size === 0) {
                size = null;
            }
            search.size = size;
            params.size = size;
        }
        if (search.entity_id !== queryParams.entity_id) {
            search.entity_id = queryParams.entity_id;
            params.entity_id = queryParams.entity_id;
        }
        if (search.entity_type !== queryParams.entity_type) {
            search.entity_type = queryParams.entity_type;
            params.entity_type = queryParams.entity_type;
        }
        if (search.sort !== queryParams.sort) {
            search.sort = queryParams.score;
            params.sort = queryParams.sort;
        }
        if (search.checkin_time !== queryParams.checkin) {
            search.checkin_time = queryParams.checkin;
            params.checkin_time = queryParams.checkin;
        }
        if (search.checkout_time !== queryParams.checkout) {
            search.checkout_time = queryParams.checkout;
            params.checkout_time = queryParams.checkout;
        }
        if (queryParams.utm_source) {
            params.utm_source = queryParams.utm_source;
        }
        if (queryParams.utm_medium) {
            params.utm_medium = queryParams.utm_medium;
        }
        if (queryParams.utm_campaign) {
            params.utm_campaign = queryParams.utm_campaign;
        }
        if (queryParams.utm_content) {
            params.utm_content = queryParams.utm_content;
        }
        if (queryParams.avail_months) {
            let availMonths = isArray(queryParams.avail_months)
                ? queryParams.avail_months
                : [queryParams.avail_months];
            availMonths = availMonths.filter((item) => {
                if (item !== 'null' && !!item) {
                    return true;
                }

                return false;
            });
            search.avail_months = availMonths;
            params.avail_months = availMonths;
        }
        if (queryParams.avail_nights) {
            search.avail_nights = queryParams.avail_nights;
            params.avail_nights = queryParams.avail_nights;
        }
        if (queryParams.flex_start_range) {
            params.flex_start_range = queryParams.flex_start_range;
        }
        if (queryParams.flex_end_range) {
            params.flex_end_range = queryParams.flex_end_range;
        }
        if (queryParams.activity_tags) {
            params.filters = [
                {
                    attribute: 'activity_tags',
                    values: [{ value: queryParams.activity_tags }],
                },
            ];
        }
        params.uiFilters = parseUiFiltersFromQuery(queryParams, { ...search.uiFilters });

        // Populate uiFilters.activityTags from URL for the new activity tags UI
        if (queryParams.activity_tags) {
            const tagValues = queryParams.activity_tags.split(',');
            const activityTagsMap = {};
            tagValues.forEach((tag) => {
                const trimmed = tag.trim();
                if (trimmed) {
                    activityTagsMap[trimmed] = toActivityTagSelection(trimmed);
                }
            });
            params.uiFilters.activityTags = activityTagsMap;
        }
        params.fg = parseFgParams(params.uiFilters, queryParams);

        // Clear camping and inventory uiFilters to avoid duplication with FG
        params.uiFilters = removeCampingFromUiFilters(params.uiFilters);
        params.inventory_type = [];

        // These parameters determine whether to show only available results.
        params.include_unavailable = true;
        params.include_notreservable = true;
        params.include_partially_available = true;
        let shouldShowOnlyAvailable = true; // default

        if (
            !isEmpty(queryParams.only_show_available) &&
            queryParams.only_show_available.toLowerCase() === 'false'
        ) {
            shouldShowOnlyAvailable = false;
        }
        const checkinDatesSet =
            (!isEmpty(search.checkin_time) && !isEmpty(search.checkout_time)) ||
            !isEmpty(search.avail_months);

        // If dates are provided and shouldShowOnlyAvailable is enabled, show only available results.
        if (checkinDatesSet && shouldShowOnlyAvailable) {
            params.include_unavailable = false;
            params.include_notreservable = false;
            params.include_partially_available = false;
        }
    }
    if (!isEmpty(params)) {
        changed = true;
    }

    dispatch(
        updateSearchCriterias({
            ...params,
        })
    );

    return { filters: params, changed };
}

// Fetch boundary data using the name of the state / city / facility
export const fetchBoundary = (boundaryType, boundaryName) => {
    return (dispatch) => {
        const params = {
            fq: [`boundary_type:${boundaryType}`],
            q: boundaryName?.toLowerCase(),
        };

        if (!boundaryType || !boundaryName) {
            return dispatch({
                type: types.GET_BOUNDARY_SEARCH_RESULT,
                payload: { boundaryResults: [] },
            });
        }
        const url = utils.urlWithParams(`${process.env.API}/search/boundaries`, params);
        return axios
            .get(url)
            .then((result) => {
                dispatch({
                    type: types.GET_BOUNDARY_SEARCH_RESULT,
                    payload: {
                        boundaryResults: result.data.results,
                    },
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_BOUNDARY_SEARCH_RESULT_ERROR,
                    payload: { boundaryResults: [] },
                });
                dispatch(searchFailed(`Error fetching boundary data: ${error}`));
            });
    };
};

// fetch and return user geoip data, could be from session store cache
export async function fetchUserGeoIp() {
    const resultString = window.sessionStorage.getItem('geoip');

    if (resultString) {
        // returned cached result
        const geoipCache = JSON.parse(resultString);
        return new Promise((resolve) => {
            resolve(geoipCache);
        });
    }

    // geocode user's ip
    return new Promise((resolve) => {
        axios
            .get(`${process.env.API}/recommendation/geoip`, {
                params: {},
            })
            .then((result) => {
                const ret = result.data;
                window.sessionStorage.setItem('geoip', JSON.stringify(ret));
                resolve(ret);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log('error fetching geoip', error);
                resolve({});
            });
    });
}

export function submitSimpleSearch(
    optionalQuery,
    optionalInventoryTypes,
    optionalSuggestion,
    optionalParentAssetId,
    optionalUIFilters,
    isAcrossUS = false,
    optionalBounds = null,
    optionalSort = null,
    optionalDates,
    omitUnavailable,
    optionalFg
) {
    return (dispatch) => {
        // reset existing filters/search state
        dispatch(resetSearchFilters());

        window.sessionStorage.removeItem('scrollManagedPaths');
        window.sessionStorage.removeItem('focusManagedPaths');

        let lat;
        let lng;
        let radius;
        let location;
        let lat_sw;
        let lng_sw;
        let lat_ne;
        let lng_ne;
        if (
            optionalSuggestion &&
            !optionalSuggestion.entity_type &&
            optionalSuggestion.lat &&
            optionalSuggestion.lng
        ) {
            // default search to suggestion location
            lat = optionalSuggestion.lat;
            lng = optionalSuggestion.lng;
            radius = DEFAULT_RADIUS;
            location = optionalSuggestion.text;

            // apply only to large states
            if (
                optionalSuggestion.lat_sw &&
                optionalSuggestion.lat_ne &&
                optionalSuggestion.lng_sw &&
                optionalSuggestion.lng_ne
            ) {
                lat = null;
                lng = null;
                lat_sw = optionalSuggestion.lat_sw;
                lng_sw = optionalSuggestion.lng_sw;
                lat_ne = optionalSuggestion.lat_ne;
                lng_ne = optionalSuggestion.lng_ne;
            }
        }

        const criteria = {
            what: optionalQuery,
            headerTextQuery: optionalQuery,
            entity_id: optionalSuggestion != null ? optionalSuggestion.entity_id : null,
            entity_type:
                optionalSuggestion != null ? optionalSuggestion.entity_type : null,
            inventory_type: optionalInventoryTypes,
            parent_asset_id: optionalParentAssetId,
            exact: false,
            lat,
            lng,
            radius,
            map_center_lat: lat,
            map_center_lng: lng,
            start: null,
            cursor_mark: null,
            location,
            placename: null,
            lat_sw,
            lng_sw,
            lat_ne,
            lng_ne,
        };

        if (optionalFg) {
            criteria.fg = optionalFg;
        }

        if (omitUnavailable) {
            criteria.include_partially_available = false;
            criteria.include_notreservable = false;
            criteria.include_unavailable = false;
        }

        // append any custom search filters
        if (optionalUIFilters) {
            Object.keys(optionalUIFilters).forEach((key) => {
                Object.keys(optionalUIFilters[key]).forEach((fltKey) => {
                    dispatch(addSearchFilter(key, optionalUIFilters[key][fltKey]));
                });
            });
        }

        if (isAcrossUS) {
            // for tours only, set bounding box for search to entire US and sort results by
            // distance/proximity to user's location
            criteria.lng_sw = -167.2815442995964;
            criteria.lat_sw = 3.6722362889127;
            criteria.lng_ne = -51.637969429829155;
            criteria.lat_ne = 60.77726184625004;
            criteria.sort = 'distance';
        }

        if (optionalBounds) {
            criteria.lat_sw = optionalBounds?.lat_sw;
            criteria.lng_sw = optionalBounds?.lng_sw;
            criteria.lat_ne = optionalBounds?.lat_ne;
            criteria.lng_ne = optionalBounds?.lng_ne;
        }

        if (optionalSort) {
            criteria.sort = optionalSort;
        }

        if (optionalDates?.startDate && optionalDates?.endDate) {
            criteria.checkin_time = searchDate(optionalDates?.startDate)
                .startOf('day')
                .format(types.CAL_DATE_FORMAT);
            criteria.checkout_time = searchDate(optionalDates?.endDate)
                .startOf('day')
                .format(types.CAL_DATE_FORMAT);
        }

        dispatch(updateSearchCriterias(criteria));
        dispatch(navigateToSearchResults());
    };
}

// Fetch inventory locations
export async function fetchNearbyInventoryLocations(
    lat = null,
    lng = null,
    inventoryTypes = [],
    size = 20,
    isAcrossUS = false,
    sort = 'score',
    additionalFilters = [],
    start = 0,
    aggregations = [],
    locationName = '',
    optionalBounds = null,
    startDate,
    endDate,
    omitUnavailable,
    radius,
    locationId
) {
    const filters = [];

    inventoryTypes.forEach((name) => {
        const inventoryTypeFilter = find(inventoryTypeFilters, { key: name });
        inventoryTypeFilter.filters.forEach((filter) => {
            const parts = filter.filterValue.split(',');
            parts.forEach((value) => {
                filters.push(`${filter.attribute}:${value}`);
            });
        });
    });

    filters.push(...additionalFilters);
    const params = {
        fq: filters,
        sort,
        start,
        size,
        agg: aggregations,
        exact: false,
    };
    if (lat && lng) {
        params.lat = lat;
        params.lng = lng;
        params.location = locationName;
    }

    if (radius) {
        params.radius = radius;
    }

    if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
    }

    if (omitUnavailable) {
        params.include_partially_available = false;
        params.include_notreservable = false;
        params.include_unavailable = false;
    }

    if (optionalBounds) {
        params.lat_sw = optionalBounds?.lat_sw;
        params.lng_sw = optionalBounds?.lng_sw;
        params.lat_ne = optionalBounds?.lat_ne;
        params.lng_ne = optionalBounds?.lng_ne;
    }

    if (isAcrossUS) {
        params.lng_sw = -167.2815442995964;
        params.lat_sw = 3.6722362889127;
        params.lng_ne = -51.637969429829155;
        params.lat_ne = 60.77726184625004;
    }
    if (locationId) {
        params.location_ids = locationId;
    }

    return axios.get(utils.urlWithParams(`${process.env.API}/search`, params));
}

// Fetch Recommendation Suggestions
export async function fetchRecommendationSuggestions(
    inventoryTypes = [],
    size = 20,
    startDate,
    endDate,
    radius,
    locationId,
    excludeIds,
    latitude,
    longitude,
    sort
) {
    const excludeList = excludeIds.map((id) => `-entity_id:${id}`);
    /* all of this is to avoid pulling timed_entry facilities */
    let ticketInventory = '';
    const hasTickets = inventoryTypes.includes('tickets');
    if (hasTickets) {
        inventoryTypes = inventoryTypes.filter((i) => i !== 'tickets');
        ticketInventory = `entity_type:ticketfacility`;
    }

    const params = {
        fg: [...inventoryTypes],
        fq: [
            `-entity_type:(tour OR timedentry_tour OR timedentry)`,
            `reservable:1`,
            ...excludeList,
            ticketInventory,
        ],
        size,
        exact: false,
    };

    if (radius) {
        params.radius = radius;
    }

    if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
    }

    if (locationId) {
        params.location_ids = locationId;
    }

    if (latitude && longitude) {
        params.lat = latitude;
        params.lng = longitude;
    }

    if (sort) {
        params.sort = sort;
    }

    return axios.get(
        utils.urlWithParams(`${process.env.API}/recommendation/suggest`, params)
    );
}

export function submitSearchFromNavigation(
    optionalQuery,
    optionalSuggestion,
    optionalUIFilters
) {
    return (dispatch, getState) => {
        const state = cloneState(getState());
        // reset existing filters/search state
        dispatch(resetSearchFilters());

        window.sessionStorage.removeItem('scrollManagedPaths');
        window.sessionStorage.removeItem('focusManagedPaths');

        let lat;
        let lng;
        let radius;
        let location;
        let lat_sw;
        let lng_sw;
        let lat_ne;
        let lng_ne;
        if (
            optionalSuggestion &&
            !optionalSuggestion.entity_type &&
            optionalSuggestion.lat &&
            optionalSuggestion.lng
        ) {
            // default search to suggestion location
            lat = optionalSuggestion.lat;
            lng = optionalSuggestion.lng;
            radius = DEFAULT_RADIUS;
            location = optionalSuggestion.text;

            // apply only to large states
            if (
                optionalSuggestion.lat_sw &&
                optionalSuggestion.lat_ne &&
                optionalSuggestion.lng_sw &&
                optionalSuggestion.lng_ne
            ) {
                lat = null;
                lng = null;
                lat_sw = optionalSuggestion.lat_sw;
                lng_sw = optionalSuggestion.lng_sw;
                lat_ne = optionalSuggestion.lat_ne;
                lng_ne = optionalSuggestion.lng_ne;
            }
        }

        const criteria = {
            what: optionalQuery,
            headerTextQuery: optionalQuery,
            entity_id: optionalSuggestion != null ? optionalSuggestion.entity_id : null,
            entity_type:
                optionalSuggestion != null ? optionalSuggestion.entity_type : null,
            exact: false,
            lat,
            lng,
            radius: radius || DEFAULT_RADIUS,
            map_center_lat: lat,
            map_center_lng: lng,
            start: null,
            cursor_mark: null,
            location,
            placename: null,
            lat_sw,
            lng_sw,
            lat_ne,
            lng_ne,
        };

        // append any custom search filters
        if (optionalUIFilters) {
            Object.keys(optionalUIFilters).forEach((key) => {
                Object.keys(optionalUIFilters[key]).forEach((fltKey) => {
                    dispatch(addSearchFilter(key, optionalUIFilters[key][fltKey]));
                });
            });
        }

        let nearByLocation = false;
        if (optionalQuery) {
            const q = optionalQuery.toLowerCase();
            nearByDictionary.forEach((word) => {
                if (q.indexOf(word) >= 0) {
                    nearByLocation = true;
                }
            });
        }

        let geocodeUserLocation = false;

        if (
            isBlank(state.search.lat) &&
            isBlank(state.search.lng) &&
            (isBlank(optionalQuery) || nearByLocation)
        ) {
            if (
                Object.hasOwn(state.search, 'use_location') &&
                !state.search.use_location
            ) {
                criteria.lat = null;
                criteria.lng = null;
                criteria.radius = null;
                criteria.location = null;
            } else {
                geocodeUserLocation = true;
            }
        }

        if (sessionStorage && geocodeUserLocation && !criteria.lat && !criteria.lng) {
            let position = sessionStorage.getItem('browserPosition');
            if (position) {
                position = JSON.parse(position);
                if (position.location && position.location.city) {
                    location = `${position.location.city}, ${position.location.region_name}`;
                } else {
                    location = null;
                }
                criteria.lat = position.latitude;
                criteria.lng = position.longitude;
                criteria.radius = null;
                criteria.location = location;
            }
        }

        dispatch(updateSearchCriterias(criteria));

        setTimeout(() => {
            const uri = buildSearchNavUri('/search', getState());
            window.location?.assign(uri);
        }, 100);
    };
}

// Fetch availability results for a specific inventory and nearby locations
export async function fetchInventoryAvailabilityResults(
    locationName,
    entityId,
    entityType,
    uiFilters,
    startDate,
    endDate,
    sort,
    includePartiallyAvailable = true,
    includeUnavailable = true,
    avail_nights,
    avail_months,
    tourTimeFilter = [],
    startRange,
    endRange
) {
    const params = {
        q: locationName,
        entity_id: entityId,
        entity_type: entityType,
        exact: true,
        fq: buildSearchFilters(uiFilters),
        include_unavailable: includeUnavailable,
        include_partially_available: includePartiallyAvailable,
        sort,
    };
    if (tourTimeFilter && tourTimeFilter.length > 0) {
        params.tour_times = tourTimeFilter;
    }

    const isoStartDate = getUTCStringFromWeirdSearchFormat(startDate);
    const isoEndDate = getUTCStringFromWeirdSearchFormat(endDate);

    if (isoStartDate && isoEndDate) {
        params.start_date = getUTCStringFromWeirdSearchFormat(isoStartDate);
        params.end_date = getUTCStringFromWeirdSearchFormat(isoEndDate);
    }

    if (avail_nights && avail_months) {
        params.avail_nights = avail_nights;
        params.avail_months = avail_months;
    }

    if (startRange) {
        params.flex_start_range = startRange;
    }

    if (endRange) {
        params.flex_end_range = endRange;
    }

    return axios.get(utils.urlWithParams(`${process.env.API}/search`, params));
}

const SEARCH_HISTORY_LIMIT = 5;

const hasSearchText = (h) => Boolean(h && h.text && h.text.length > 0);

/** Newest `last_used` first; ties keep their order (Array#sort is stable). */
function byMostRecentlyUsed(a, b) {
    if (a.last_used === b.last_used) {
        return 0;
    }
    return a.last_used > b.last_used ? -1 : 1;
}

/** Drops entries without text and keeps the first entry seen for each `text`. */
function dedupeSearchHistory(items) {
    const seenTexts = new Set();
    return items.filter((item) => {
        if (!hasSearchText(item) || seenTexts.has(item.text)) {
            return false;
        }
        seenTexts.add(item.text);
        return true;
    });
}

/**
 * Loads the most recent search history entries from localStorage and, for a registered
 * user, merges them with the server's history: local entries the server doesn't have yet
 * are posted to the API, and the merged list is written back to localStorage.
 *
 * Falls back to local history if the API call fails. Mirrors sarsaparilla's
 * `SearchActions.loadSearchHistoryItems`, minus its in-memory cache, and with three fixes
 * sarsa doesn't have yet: the newest copy of a duplicated entry wins (sarsa keeps the local
 * one), entries are marked synced once posted (sarsa re-posts them on every load), and a
 * non-array `search_history` is reset rather than returned.
 */
export async function loadSearchHistoryItems() {
    let storedHistory = [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem('search_history') || '[]');
        if (!Array.isArray(parsed)) {
            throw new TypeError('search_history is not an array');
        }
        storedHistory = parsed
            .filter(hasSearchText)
            .sort(byMostRecentlyUsed)
            .slice(0, SEARCH_HISTORY_LIMIT);
    } catch (e) {
        storedHistory = [];
        window.localStorage.setItem('search_history', JSON.stringify([]));
    }

    if (isRegisteredUserLoggedIn()) {
        try {
            const res = await axios.get(`${process.env.API}/search/history`, {
                headers: { Authorization: getAuthHeader() },
            });
            if (res.data && res.data.items) {
                const loaded = res.data.items.map((it) => ({ ...it, synced: true }));
                // Sort before deduping so the newest copy of each entry wins; on a tie the
                // local copy (listed first) wins
                let items = dedupeSearchHistory(
                    [...storedHistory, ...loaded].sort(byMostRecentlyUsed)
                ).slice(0, SEARCH_HISTORY_LIMIT);

                // save to backend any new items that exist locally
                const toSave = items.filter((it) => !it.synced);
                if (toSave.length > 0) {
                    try {
                        await axios.post(
                            `${process.env.API}/search/history`,
                            {
                                items: toSave,
                            },
                            {
                                headers: { Authorization: getAuthHeader() },
                            }
                        );
                        items = items.map((it) =>
                            it.synced ? it : { ...it, synced: true }
                        );
                    } catch (e) {
                        // ignore/log error
                        // eslint-disable-next-line no-console
                        console.log('could not save local history', e);
                    }
                }

                window.localStorage.setItem('search_history', JSON.stringify(items));
                return items;
            }
        } catch (e) {
            // fall back to local history
            // eslint-disable-next-line no-console
            console.log('error fetching search history from API', e);
        }
    }

    return Promise.resolve(storedHistory);
}

/**
 * Records a search in history: refreshes `last_used` on the matching entry (or appends a
 * new one), syncs it to the API when logged in, and persists the list to localStorage.
 *
 * Never edits `searchHistory` or its entries in place — the array is React state, and a
 * selected history entry is also held in Redux as `search.selectedSuggestion`.
 */
export async function addSearchHistoryItem({ searchHistory, rawSearchText, suggestion }) {
    const loadedSearchHistory = [...(searchHistory || (await loadSearchHistoryItems()))];
    const searchText = typeof rawSearchText === 'string' ? rawSearchText.trim() : '';

    let matchIndex;
    let newEntry;
    if (suggestion) {
        matchIndex = loadedSearchHistory.findIndex(
            (it) =>
                it.entity_id === suggestion.entity_id &&
                it.entity_type === suggestion.entity_type &&
                it.name === suggestion.name &&
                it.text === suggestion.text
        );
        newEntry = suggestion;
    } else if (searchText.length > 0) {
        matchIndex = loadedSearchHistory.findIndex((it) => it.text === searchText);
        newEntry = { text: searchText };
    } else {
        return Promise.resolve();
    }

    const isNewEntry = matchIndex === -1;
    let toSave = {
        ...(isNewEntry ? newEntry : loadedSearchHistory[matchIndex]),
        last_used: new Date().toISOString(),
    };

    if (isRegisteredUserLoggedIn()) {
        try {
            await axios.post(
                `${process.env.API}/search/history`,
                {
                    items: [toSave],
                },
                {
                    headers: { Authorization: getAuthHeader() },
                }
            );
            toSave = { ...toSave, synced: true };
        } catch (e) {
            // ignore errors
            // eslint-disable-next-line no-console
            console.log('could not save local history', e);
        }
    }

    if (isNewEntry) {
        loadedSearchHistory.push(toSave);
    } else {
        loadedSearchHistory[matchIndex] = toSave;
    }
    window.localStorage.setItem('search_history', JSON.stringify(loadedSearchHistory));
    return Promise.resolve();
}

export async function deleteSearchHistoryItems() {
    window.localStorage.setItem('search_history', JSON.stringify([]));
    if (isRegisteredUserLoggedIn()) {
        await axios.delete(`${process.env.API}/search/history`, {
            headers: { Authorization: getAuthHeader() },
        });
    }
    return new Promise((resolve) => {
        resolve();
    });
}

// cSpell:ignore Criterias aggs checkin geoip notreservable giftcard placeid recarea activitypass ticketfacility tickettours timedentry dayuse nearme treepermit vehiclepermit venuereservations, fcfs
